import { LeaseType, VehicleTypes } from '@at/design-system-patterns';
import { calculateDeposit, getCheapestAndFastestDeliveryLeaseOffer, getDeliveryEstimateBadgeLabel, } from '@at/leasing-utils';
import { constructLeaseOption } from '@at/leasing-utils/lib/leasing-utils/lease-option-utils';
import { getImgResizedCDNURL } from '@at/formatter-utils/lib/url-utils/uri.utils';
import { BadgeCategories } from '@at/design-system-elements';
const resizeImages = (images) => {
    if (!images)
        return undefined;
    return images.map((imgData) => (Object.assign(Object.assign({}, imgData), { url: getImgResizedCDNURL(imgData.url, 600) })));
};
const getSpecificationData = (specification) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const specificationData = {
        seats: specification.seats ? `${(_a = specification.seats) === null || _a === void 0 ? void 0 : _a.toString()} seats` : undefined,
        doors: specification.doors ? `${(_b = specification.doors) === null || _b === void 0 ? void 0 : _b.toString()} doors` : undefined,
        bodyType: specification.bodyType || undefined,
        fuelType: ((_c = specification.fuel) === null || _c === void 0 ? void 0 : _c.toLowerCase().includes('hybrid')) ? 'Hybrid' : specification.fuel || undefined,
        transmission: specification.transmission || undefined,
    };
    if ((((_d = specification.fuel) === null || _d === void 0 ? void 0 : _d.includes('Electric')) || ((_e = specification.fuel) === null || _e === void 0 ? void 0 : _e.includes('Hydrogen'))) &&
        specification.techData) {
        specificationData.batteryRange = ((_g = (_f = specification.battery) === null || _f === void 0 ? void 0 : _f.range) === null || _g === void 0 ? void 0 : _g.range)
            ? `${specification.battery.range.range} ${specification.battery.range.unit}`
            : undefined;
    }
    else {
        specificationData.engineSize = ((_h = specification.techData) === null || _h === void 0 ? void 0 : _h.badgeEngineSize) || undefined;
    }
    return specificationData;
};
export const mapGQLAdDataToVehicleCards = ({ advertPath, data, vehicleType, vehicleCardData, additionalVehicleCardData, queryParams = '', deliveryEstimateBadge = false, }) => {
    var _a;
    return (_a = data === null || data === void 0 ? void 0 : data.search.adverts.advertList.adverts.map((advert, index) => {
        var _a, _b, _c, _d, _e, _f;
        const { id, price, rrp, saving, coreSpecification, additionalSpecification, priceIndicatorRating, priceIndicatorRatingLabel, video: adVideo, leasingOffers, } = advert;
        const advertId = id;
        const title = (_a = coreSpecification.derivative) !== null && _a !== void 0 ? _a : '';
        const type = vehicleType === VehicleTypes.NEW && !saving ? VehicleTypes.USED : vehicleType;
        const nonNullLeasingOffers = leasingOffers && [...leasingOffers].filter(Boolean);
        const leaseType = additionalVehicleCardData === null || additionalVehicleCardData === void 0 ? void 0 : additionalVehicleCardData.leaseType;
        const leaseTypeString = leaseType === LeaseType.BUSINESS ? 'business' : 'personal';
        const cheapestAndFastestDeliveryLeaseOffer = nonNullLeasingOffers && getCheapestAndFastestDeliveryLeaseOffer(nonNullLeasingOffers, leaseTypeString);
        const leaseOptionParam = cheapestAndFastestDeliveryLeaseOffer && type === VehicleTypes.LEASE
            ? `leaseOption=${constructLeaseOption({
                depositMultiple: cheapestAndFastestDeliveryLeaseOffer.depositMonths,
                numberOfMonths: cheapestAndFastestDeliveryLeaseOffer.termMonths,
                mileage: cheapestAndFastestDeliveryLeaseOffer.totalMileage,
            })}`
            : '';
        const queryParamsStringWithLeaseOption = [leaseOptionParam, queryParams].filter(Boolean).join('&');
        const completeQueryParams = queryParamsStringWithLeaseOption ? `?${queryParamsStringWithLeaseOption}` : '';
        const leasingLink = `/${advertPath}/leasing/product/${advert.id}${completeQueryParams}`;
        const nonLeasingLink = `/${advertPath}-details/${advertId}${completeQueryParams}`;
        const link = type === VehicleTypes.LEASE ? leasingLink : nonLeasingLink;
        const priceUpfront = cheapestAndFastestDeliveryLeaseOffer
            ? calculateDeposit(cheapestAndFastestDeliveryLeaseOffer, leaseType === LeaseType.BUSINESS)
            : null;
        const priceMonthly = (leaseType === LeaseType.BUSINESS && (cheapestAndFastestDeliveryLeaseOffer === null || cheapestAndFastestDeliveryLeaseOffer === void 0 ? void 0 : cheapestAndFastestDeliveryLeaseOffer.monthlyPriceExVat)) ||
            (cheapestAndFastestDeliveryLeaseOffer === null || cheapestAndFastestDeliveryLeaseOffer === void 0 ? void 0 : cheapestAndFastestDeliveryLeaseOffer.monthlyPrice);
        const imageList = resizeImages((_b = advert.imageList) === null || _b === void 0 ? void 0 : _b.images);
        const video = adVideo && {
            url: (_c = adVideo === null || adVideo === void 0 ? void 0 : adVideo.url) !== null && _c !== void 0 ? _c : '',
            preview: (_d = adVideo === null || adVideo === void 0 ? void 0 : adVideo.preview) !== null && _d !== void 0 ? _d : '',
        };
        const vehicle = {
            make: (_e = coreSpecification.make) !== null && _e !== void 0 ? _e : undefined,
            model: (_f = coreSpecification.model) !== null && _f !== void 0 ? _f : undefined,
        };
        const { depositMonths, termMonths, totalMileage } = cheapestAndFastestDeliveryLeaseOffer !== null && cheapestAndFastestDeliveryLeaseOffer !== void 0 ? cheapestAndFastestDeliveryLeaseOffer : {};
        const badgeItems = deliveryEstimateBadge && cheapestAndFastestDeliveryLeaseOffer
            ? [
                {
                    text: getDeliveryEstimateBadgeLabel(cheapestAndFastestDeliveryLeaseOffer === null || cheapestAndFastestDeliveryLeaseOffer === void 0 ? void 0 : cheapestAndFastestDeliveryLeaseOffer.deliveryEstimateDays),
                    category: BadgeCategories.default,
                },
            ]
            : undefined;
        const priceIndicatorRatingTyped = priceIndicatorRating;
        return Object.assign(Object.assign({ badgeItems: deliveryEstimateBadge ? badgeItems : undefined, advertId,
            type,
            imageList,
            price, priceIndicatorRating: priceIndicatorRatingTyped, priceIndicatorRatingLabel,
            link,
            rrp,
            saving,
            title,
            video,
            vehicle,
            priceMonthly,
            priceUpfront,
            depositMonths,
            termMonths,
            totalMileage, specificationData: additionalSpecification ? getSpecificationData(additionalSpecification) : undefined }, additionalVehicleCardData), vehicleCardData === null || vehicleCardData === void 0 ? void 0 : vehicleCardData[index]);
    }).filter((advert) => advert != null)) !== null && _a !== void 0 ? _a : [];
};
